import React, {
  createContext,
  useState,
  useContext,
} from 'react';

interface EventProviderProps {
  children: React.ReactNode;
}
interface EventHandler {
  (data: any): void
}
interface Listeners {
  [key: string]: Array<EventHandler>;
}

export interface EmitEvent {
  (event: string, data: any): void
}

export interface AddEvent {
  (event: string, callback: EventHandler): void
}

export interface RemoveEvent {
  (event: string, callback: EventHandler): void
}

export interface EventContextType {
  emit: EmitEvent;
  on: AddEvent;
  remove: RemoveEvent;
}

const defaultEventContext: EventContextType = {
  emit: () => { },
  on: () => { },
  remove: () => { },
};

const EventContext = createContext<EventContextType>(defaultEventContext);

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const [listeners, setListeners] = useState<Listeners>({});

  const emit: EmitEvent = (event, data) => {
    listeners[event]?.forEach((callback: EventHandler) => callback?.(data));
  };

  const on: AddEvent = (event, callback) => {
    setListeners((prevListeners: Listeners) => ({
      ...prevListeners,
      [event]: [...(prevListeners[event] || []), callback],
    }));
  };

  const remove: RemoveEvent = (event, callback) => {
    setListeners((prevListeners: Listeners) => ({
      ...prevListeners,
      [event]: prevListeners[event]?.filter((handler) => handler !== callback),
    }));
  };

  const value = React.useMemo(() => ({ emit, on, remove }), [emit, on, remove]);

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = (): EventContextType => {
  const context = useContext(EventContext);

  if (!context) {
    throw new Error('useEventContext must be used within an EventProvider');
  }

  return context;
};
